/**
 * @desc: 内置筛选控件预览工具函数
 */
import React from 'react';
import { LOCALE_CN } from '@home/components/NewRegionSelect/config';

import type { IFilterItemConfig } from '@intelligence/v3/filterCore/types';

export const OPT_TEXT_MAP: Record<string, string> = {
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  eq: '=',
  '>': '>',
  '>=': '≥',
  '<': '<',
  '<=': '≤',
  '=': '=',
};

const PREVIEW_FALLBACK_MAX_LENGTH = 60;

export const renderImgText = (text: string, img = '', isLastOne = false): React.ReactNode => {
  return (
    <>
      {img && <img style={{ width: 16, verticalAlign: 'middle', marginRight: 4 }} src={img} alt="" />}
      <span>{text}</span>
      {isLastOne ? null : <span style={{ marginRight: 6 }}>,</span>}
    </>
  );
};

export const getOptionLocaleText = (option: any, locale: string): string => {
  if (!option) {
    return '';
  }
  if (locale === LOCALE_CN) {
    return option?.textCN || option?.text || option?.textEN || option?.name || option?.value || '';
  }
  return option?.text || option?.textEN || option?.name || option?.value || '';
};

export const getSafePreviewFallbackText = (value: unknown, emptyText = '-'): string => {
  if (value === undefined || value === null || value === '') {
    return emptyText;
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  try {
    const serializedValue = JSON.stringify(value);

    if (serializedValue) {
      return serializedValue.slice(0, PREVIEW_FALLBACK_MAX_LENGTH);
    }
  } catch (_error) {}

  return Object.prototype.toString.call(value);
};

export const renderOptionSummary = (
  value: any,
  options: any[],
  formatMessage: Function,
  locale?: string,
): React.ReactNode => {
  const result = typeof value === 'string' ? [value] : Array.isArray(value) ? value : [];
  const resolvedOptions = Array.isArray(options) ? options : [];
  const newOptions = resolvedOptions.filter((item) => result.includes(item?.value));

  if (result.length === 0) {
    return formatMessage({ id: 'dbi_filterSave_none' });
  }

  if (result.length > 1 && newOptions.length === result.length && result.length === resolvedOptions.length) {
    return formatMessage({ id: 'dbi_all' });
  }
  if (result.length > 2) {
    return result.length;
  }

  return result.map((valueItem, index) => {
    const option = resolvedOptions.find((item) => item?.value === valueItem);
    const text = (locale ? getOptionLocaleText(option, locale) : '') || option?.text || option?.textEN || option?.name || String(valueItem);

    return (
      <React.Fragment key={option?.value ?? valueItem ?? index}>
        {renderImgText(text, option?.img, index === result.length - 1)}
      </React.Fragment>
    );
  });
};

export const getPreviewTitle = (item: IFilterItemConfig, formatMessage: Function): string => {
  const title = item.controlProps?.title;
  const selectorLabel = item.controlProps?.selectorConfig?.label;
  const label = item.label || selectorLabel || item.field;

  if (typeof title === 'string' && title.startsWith('dbi_')) {
    return formatMessage({ id: title });
  }
  if (typeof label === 'string' && label.startsWith('dbi_')) {
    return formatMessage({ id: label });
  }
  return title || label;
};

/** 获取 region options 中所有国家子选项 */
export const getRegionSubOptions = (options: any[]): any[] => {
  const allChildren: any[] = [];
  options?.forEach((item: any) => {
    item?.country_set?.forEach((subItem: any) => {
      allChildren.push({
        value: subItem?.country_code,
        text: subItem?.country_name_en,
        textCN: subItem?.country_name_ch,
      });
    });
  });
  return allChildren;
};
